import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Receipt, User, MapPin, UserCog, Wrench, Printer, RotateCcw, Shield, AlertCircle, FileText, CheckCircle2, X } from 'lucide-react';
import ThermalTicket from '../components/ThermalTicket';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = { return: 'Devolución', exchange: 'Cambio', warranty: 'Garantía' };
const TYPE_COLOR = {
  return: 'bg-rose-100 text-rose-700',
  exchange: 'bg-amber-100 text-amber-700',
  warranty: 'bg-emerald-100 text-emerald-700',
};

// CFDI 4.0 catalogues (most common)
const SAT_REGIMES = [
  ['601', 'General de Ley Personas Morales'],
  ['603', 'Personas Morales con Fines no Lucrativos'],
  ['605', 'Sueldos y Salarios'],
  ['606', 'Arrendamiento'],
  ['612', 'Personas Físicas con Actividades Empresariales y Profesionales'],
  ['616', 'Sin obligaciones fiscales'],
  ['621', 'Incorporación Fiscal'],
  ['626', 'Régimen Simplificado de Confianza'],
];
const CFDI_USES = [
  ['G01', 'Adquisición de mercancías'],
  ['G02', 'Devoluciones, descuentos o bonificaciones'],
  ['G03', 'Gastos en general'],
  ['I04', 'Equipo de cómputo y accesorios'],
  ['I08', 'Otra maquinaria y equipo'],
  ['P01', 'Por definir'],
  ['S01', 'Sin efectos fiscales'],
];
const PAYMENT_FORMS = [
  ['01', 'Efectivo'],
  ['02', 'Cheque nominativo'],
  ['03', 'Transferencia electrónica'],
  ['04', 'Tarjeta de crédito'],
  ['28', 'Tarjeta de débito'],
  ['99', 'Por definir'],
];
const PAYMENT_METHODS = [
  ['PUE', 'Pago en una sola exhibición'],
  ['PPD', 'Pago en parcialidades o diferido'],
];

export default function SaleDetail() {
  const { id } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  // Selection: { itemId: qty }
  const [selected, setSelected] = useState({});
  // 'return' | 'warranty' | null  (exchange chosen later from reason picker)
  const [action, setAction] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  // Existing returns linked to this sale
  const [returns, setReturns] = useState([]);
  // Invoice (Factura) modal
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    fiscal_name: '', fiscal_rfc: '', fiscal_regime: '', fiscal_zip: '', fiscal_email: '',
    cfdi_use: '', payment_form: '01', payment_method: 'PUE', notes: '',
  });

  useEffect(() => { fetchSale(); fetchBranches(); fetchReturns(); fetchInvoices(); /* eslint-disable-next-line */ }, [id]);

  const fetchSale = async () => {
    try {
      const res = await axios.get(`${API}/api/pos/sales/${id}`, { headers: getAuthHeaders() });
      setSale(res.data);
    } catch { navigate('/sales'); }
    setLoading(false);
  };
  const fetchBranches = async () => {
    try { const r = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() }); setBranches(r.data); } catch { /* noop */ }
  };
  const fetchReturns = async () => {
    try { const r = await axios.get(`${API}/api/returns?sale_id=${id}`, { headers: getAuthHeaders() }); setReturns(r.data); } catch { /* noop */ }
  };
  const fetchInvoices = async () => {
    try { const r = await axios.get(`${API}/api/invoices?sale_id=${id}`, { headers: getAuthHeaders() }); setInvoices(r.data); } catch { /* noop */ }
  };

  const openInvoice = async () => {
    setInvoiceError('');
    // Pre-fill from the linked client's fiscal data
    let pref = { fiscal_name: '', fiscal_rfc: '', fiscal_regime: '', fiscal_zip: '', fiscal_email: '' };
    if (sale?.client_id) {
      try {
        const c = await axios.get(`${API}/api/clients/${sale.client_id}`, { headers: getAuthHeaders() });
        pref = {
          fiscal_name: c.data.fiscal_name || c.data.name || '',
          fiscal_rfc: c.data.fiscal_rfc || '',
          fiscal_regime: c.data.fiscal_regime || '',
          fiscal_zip: c.data.fiscal_zip || '',
          fiscal_email: c.data.fiscal_email || c.data.email || '',
        };
      } catch { /* noop */ }
    }
    setInvoiceForm(f => ({ ...f, ...pref }));
    setInvoiceOpen(true);
  };

  const submitInvoice = async (e) => {
    e?.preventDefault?.();
    setInvoiceError('');
    setInvoiceSaving(true);
    try {
      await axios.post(`${API}/api/invoices/request`, { sale_id: sale.id, ...invoiceForm }, { headers: getAuthHeaders() });
      setInvoiceOpen(false);
      fetchInvoices();
      fetchSale();
    } catch (err) {
      setInvoiceError(err.response?.data?.detail || 'No se pudo registrar la solicitud');
    }
    setInvoiceSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Cargando...</div>;
  if (!sale) return null;

  const branchName = branches.find(b => b.id === sale.branch_id)?.name || sale.branch_id || '-';
  const isPos = (sale.sale_type || 'pos') === 'pos';
  const processedKeys = new Set(sale.processed_item_keys || []);
  // Item keys that currently belong to an open draft return for this sale.
  // The backend will reopen the existing draft instead of creating a new one
  // when a drafted item is re-selected.
  const draftItemKeys = new Set(
    (returns || [])
      .filter(r => r.status === 'draft')
      .flatMap(r => (r.selected_items || []).map(it => it.id))
      .filter(Boolean)
  );

  // Compute time windows
  const createdAt = new Date(sale.created_at);
  const hoursSince = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
  const returnOpen = hoursSince <= 48;
  const warrantyOpen = hoursSince <= 31 * 24;

  const toggleItem = (item) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[item.id] != null) {
        delete next[item.id];
      } else {
        next[item.id] = item.quantity || 1;
      }
      return next;
    });
  };
  const updateQty = (itemId, v, max) => {
    const num = Math.max(1, Math.min(parseInt(v, 10) || 1, max));
    setSelected(prev => ({ ...prev, [itemId]: num }));
  };

  const selectedCount = Object.keys(selected).length;

  const beginAction = (type) => {
    setError('');
    if (selectedCount === 0) { setError('Selecciona al menos un artículo'); return; }
    setAction(type);
  };

  const submit = async (reason) => {
    setError('');
    setCreating(true);
    // Determine final type: 'return' button creates either 'return' (no exchange items)
    // or 'exchange' (when client takes new merchandise). We start as 'return' and
    // the ReturnDetail screen lets them add items which auto-promotes to exchange.
    const type = action === 'warranty' ? 'warranty' : 'return';
    const items = Object.entries(selected).map(([itemId, qty]) => ({ id: itemId, quantity: qty }));
    try {
      const res = await axios.post(`${API}/api/returns`, {
        sale_id: sale.id, type, reason, items,
      }, { headers: getAuthHeaders() });
      navigate(`/returns/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo crear el registro');
      setCreating(false);
    }
  };

  return (
    <div data-testid="sale-detail-page">
      <Link to="/sales" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1D3C6E] mb-4 transition-colors no-underline">
        <ArrowLeft className="w-4 h-4" /> Volver al historial
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">{sale.ticket || `Venta ${sale.id?.slice(-8)}`}</h1>
          <p className="text-sm text-slate-500">{createdAt.toLocaleString('es-MX')} — <span className={`status-badge ${sale.sale_type === 'repair' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{sale.sale_type === 'repair' ? 'Reparacion' : 'POS'}</span></p>
        </div>
        <div className="flex items-center gap-3">
          {!sale.is_refund && (sale.sale_type === 'pos' || sale.sale_type === 'repair') && (
            <button onClick={openInvoice} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#AFBD20] bg-[#AFBD20]/10 text-sm font-medium text-[#1D3C6E] hover:bg-[#AFBD20]/20" data-testid="generate-invoice-btn">
              <FileText className="w-4 h-4" /> Generar Factura
              {sale.invoice_status === 'pending' && <span className="ml-1 inline-flex items-center text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">Solicitada</span>}
              {sale.invoice_status === 'issued' && <span className="ml-1 inline-flex items-center text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">Emitida</span>}
            </button>
          )}
          <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-[#1D3C6E] hover:bg-slate-50" data-testid="print-sale-ticket-btn">
            <Printer className="w-4 h-4" /> Imprimir Ticket
          </button>
          <div className="text-right">
            <p className="text-3xl font-bold text-[#1D3C6E]">${(sale.total || 0).toFixed(2)}</p>
            <p className="text-xs text-slate-500">Total de la venta</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2 font-['Outfit']">
              <Receipt className="w-4 h-4 text-[#1D3C6E]" /> Detalle de Productos / Servicios
            </h3>
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 font-medium text-slate-500 w-8"></th>
                  <th className="text-left py-2 font-medium text-slate-500">Articulo</th>
                  <th className="text-right py-2 font-medium text-slate-500 w-28">Cant.</th>
                  <th className="text-right py-2 font-medium text-slate-500 w-24">Precio</th>
                  <th className="text-right py-2 font-medium text-slate-500 w-24">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(sale.items || []).map((item, idx) => {
                  const itemId = item.id || `idx-${idx}`;
                  const sel = selected[itemId];
                  const isProcessed = processedKeys.has(itemId);
                  const inDraft = draftItemKeys.has(itemId);
                  return (
                    <tr key={itemId} className={`border-b border-slate-100 ${sel != null ? 'bg-amber-50/40' : ''} ${isProcessed ? 'opacity-60' : ''}`}>
                      <td className="py-2.5 align-top">
                        {isPos && (
                          <input type="checkbox" className="w-4 h-4 accent-[#1D3C6E] cursor-pointer disabled:cursor-not-allowed"
                            checked={sel != null}
                            disabled={isProcessed}
                            onChange={() => !isProcessed && toggleItem({ ...item, id: itemId })}
                            data-testid={`sale-item-check-${idx}`} />
                        )}
                      </td>
                      <td className="py-2.5">
                        <p className="font-medium">{item.name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {item.type && <p className="text-[10px] text-slate-400 capitalize">{item.type === 'part' ? 'Refaccion' : item.type === 'accessory' ? 'Accesorio' : item.type === 'service' ? 'Servicio' : item.type}</p>}
                          {isProcessed && (
                            <span className="inline-flex items-center text-[10px] font-medium text-slate-600 bg-slate-200 rounded px-1.5 py-0.5" data-testid={`sale-item-processed-${idx}`}>
                              Ya procesado
                            </span>
                          )}
                          {!isProcessed && inDraft && (
                            <span className="inline-flex items-center text-[10px] font-medium text-amber-700 bg-amber-100 rounded px-1.5 py-0.5" data-testid={`sale-item-draft-${idx}`}>
                              En borrador
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 text-right">
                        {sel != null ? (
                          <input type="number" min={1} max={item.quantity || 1}
                            value={sel}
                            onChange={(e) => updateQty(itemId, e.target.value, item.quantity || 1)}
                            className="w-16 text-right border border-slate-200 rounded px-1.5 py-0.5 text-sm"
                            data-testid={`sale-item-qty-${idx}`} />
                        ) : <span className="text-slate-600">{item.quantity}</span>}
                        <span className="text-[10px] text-slate-400 ml-1">/ {item.quantity}</span>
                      </td>
                      <td className="py-2.5 text-right text-slate-600">${(item.price || 0).toFixed(2)}</td>
                      <td className="py-2.5 text-right font-medium">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-slate-200 mt-2 pt-3 space-y-1">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span>${(sale.subtotal || 0).toFixed(2)}</span></div>
              {(sale.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-sm"><span className="text-slate-500">Descuento {sale.discount_type === 'percentage' ? `(${sale.discount_value}%)` : ''}</span><span className="text-red-500">-${(sale.discount_amount || 0).toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-base font-bold border-t border-slate-100 pt-2"><span>Total</span><span className="text-[#1D3C6E]">${(sale.total || 0).toFixed(2)}</span></div>
            </div>

            {/* Action buttons — only available on POS sales */}
            {isPos && (
            <>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200">
              <button
                onClick={() => beginAction('return')}
                disabled={!returnOpen || selectedCount === 0 || creating}
                title={!returnOpen ? 'Plazo de devolución vencido' : (selectedCount === 0 ? 'Selecciona al menos un artículo' : '')}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md text-sm font-medium bg-[#1D3C6E] hover:bg-[#152B50] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="sale-action-return"
              >
                <RotateCcw className="w-4 h-4" /> Devolución/Cambio
              </button>
              <button
                onClick={() => beginAction('warranty')}
                disabled={!warrantyOpen || selectedCount === 0 || creating}
                title={!warrantyOpen ? 'Plazo de garantía vencido' : (selectedCount === 0 ? 'Selecciona al menos un artículo' : '')}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="sale-action-warranty"
              >
                <Shield className="w-4 h-4" /> Garantía
              </button>
              {!returnOpen && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-500"><AlertCircle className="w-3.5 h-3.5" /> Plazo de devolución vencido</span>
              )}
              {!warrantyOpen && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-500"><AlertCircle className="w-3.5 h-3.5" /> Plazo de garantía vencido</span>
              )}
            </div>
            {error && <p className="text-xs text-rose-600 mt-2" data-testid="sale-action-error">{error}</p>}

            {/* Inline reason picker */}
            {action && (
              <div className="mt-4 border border-[#1D3C6E]/20 rounded-lg p-4 bg-[#1D3C6E]/5" data-testid="reason-picker">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-[#1D3C6E]">
                    Motivo de la {action === 'warranty' ? 'garantía' : 'devolución/cambio'}
                  </h4>
                  <button onClick={() => setAction(null)} className="text-xs text-slate-500 hover:underline">Cancelar</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button
                    onClick={() => submit('change_of_mind')}
                    disabled={creating}
                    className="flex items-start gap-2 p-3 rounded-md border border-slate-200 bg-white hover:border-[#1D3C6E] hover:bg-[#1D3C6E]/5 text-left disabled:opacity-50"
                    data-testid="reason-change"
                  >
                    <RotateCcw className="w-4 h-4 mt-0.5 text-[#1D3C6E]" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">Cambió de opinión</p>
                      <p className="text-xs text-slate-500">El cliente decidió no quedarse con los artículos.</p>
                    </div>
                  </button>
                  <button
                    onClick={() => submit('does_not_fit')}
                    disabled={creating}
                    className="flex items-start gap-2 p-3 rounded-md border border-slate-200 bg-white hover:border-[#1D3C6E] hover:bg-[#1D3C6E]/5 text-left disabled:opacity-50"
                    data-testid="reason-does-not-fit"
                  >
                    <AlertCircle className="w-4 h-4 mt-0.5 text-rose-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">No sirve / No le queda</p>
                      <p className="text-xs text-slate-500">El producto no funciona o no es compatible.</p>
                    </div>
                  </button>
                </div>
              </div>
            )}
            </>
            )}
          </div>

          {/* Existing returns linked to this sale */}
          {returns.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Devoluciones y garantías de esta venta</h3>
              <div className="space-y-2">
                {returns.map(r => (
                  <Link key={r.id} to={`/returns/${r.id}`} className="flex items-center justify-between p-2.5 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors no-underline">
                    <div className="flex items-center gap-2">
                      <span className={`status-badge ${TYPE_COLOR[r.type] || 'bg-slate-100 text-slate-700'}`}>{TYPE_LABEL[r.type] || r.type}</span>
                      <span className="text-xs text-slate-600">{new Date(r.created_at).toLocaleString('es-MX')}</span>
                      <span className="text-xs text-slate-400">{r.status === 'draft' ? 'Borrador' : 'Completado'}</span>
                    </div>
                    <span className={`text-sm font-semibold ${(r.resolution_amount || 0) < 0 ? 'text-rose-600' : 'text-[#1D3C6E]'}`}>
                      {(r.resolution_amount || 0) < 0 ? '-' : ''}${Math.abs(r.resolution_amount || 0).toFixed(2)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {sale.repair_id && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">Esta venta incluye un cargo de reparacion</span>
              </div>
              <Link to={`/repairs/${sale.repair_id}`} className="text-sm font-medium text-[#1D3C6E] hover:underline inline-flex items-center gap-1 no-underline" data-testid="view-repair-link">
                Ver reparacion <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
              </Link>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Cliente</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><User className="w-4 h-4 text-slate-400" /> {sale.client_name || 'Venta general'}</div>
              {sale.client_id && <p className="text-xs text-slate-400">ID: {sale.client_id}</p>}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Pago</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Metodo</span><span className="font-medium">{sale.payment_method}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Monto pagado</span><span className="font-medium text-green-600">${(sale.amount_paid || 0).toFixed(2)}</span></div>
              {(sale.amount_paid || 0) < (sale.total || 0) && (
                <div className="flex justify-between"><span className="text-slate-500">Pendiente</span><span className="font-medium text-red-600">${((sale.total || 0) - (sale.amount_paid || 0)).toFixed(2)}</span></div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Detalles</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-400" /> {branchName}</div>
              <div className="flex items-center gap-2 text-slate-700"><UserCog className="w-4 h-4 text-slate-400" /> {sale.sold_by || '-'}</div>
            </div>
          </div>

          {invoices.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="invoice-history-section">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit'] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#1D3C6E]" /> Facturación
              </h3>
              <div className="space-y-2">
                {invoices.map(inv => (
                  <div key={inv.id} className="text-xs bg-slate-50 rounded p-2.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">{inv.fiscal_rfc}</span>
                      <span className={`inline-flex items-center text-[10px] font-semibold rounded px-1.5 py-0.5 ${
                        inv.status === 'issued' ? 'bg-emerald-100 text-emerald-700'
                        : inv.status === 'cancelled' ? 'bg-slate-200 text-slate-600'
                        : inv.status === 'failed' ? 'bg-rose-100 text-rose-700'
                        : 'bg-amber-100 text-amber-700'
                      }`}>
                        {inv.status === 'issued' ? 'Emitida' : inv.status === 'cancelled' ? 'Cancelada' : inv.status === 'failed' ? 'Fallida' : 'Pendiente'}
                      </span>
                    </div>
                    <p className="text-slate-500 truncate">{inv.fiscal_name}</p>
                    <p className="text-[10px] text-slate-400">CFDI {inv.cfdi_use} · {inv.payment_method} · {inv.payment_form}</p>
                    {inv.uuid && <p className="text-[10px] text-slate-400 break-all">UUID: {inv.uuid}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invoice request modal */}
      {invoiceOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="invoice-modal">
          <form onSubmit={submitInvoice} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-[#1D3C6E] font-['Outfit'] flex items-center gap-2">
                <FileText className="w-5 h-5" /> Generar Factura — Venta {sale.ticket}
              </h2>
              <button type="button" onClick={() => setInvoiceOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="invoice-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {!sale.client_id && (
                <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Esta venta no tiene cliente asociado. Captura los datos fiscales a continuación.</span>
                </div>
              )}
              <p className="text-xs font-semibold uppercase tracking-wider text-[#1D3C6E]">Datos del Receptor</p>
              <div>
                <label className="text-xs font-medium text-slate-600">Nombre / Razón Social *</label>
                <input required value={invoiceForm.fiscal_name} onChange={(e) => setInvoiceForm(f => ({ ...f, fiscal_name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="invoice-fiscal-name" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">RFC *</label>
                  <input required value={invoiceForm.fiscal_rfc}
                    onChange={(e) => setInvoiceForm(f => ({ ...f, fiscal_rfc: e.target.value.toUpperCase() }))}
                    maxLength={13} className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="invoice-fiscal-rfc" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Código Postal *</label>
                  <input required value={invoiceForm.fiscal_zip}
                    onChange={(e) => setInvoiceForm(f => ({ ...f, fiscal_zip: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                    maxLength={5} className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="invoice-fiscal-zip" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Régimen Fiscal *</label>
                <select required value={invoiceForm.fiscal_regime}
                  onChange={(e) => setInvoiceForm(f => ({ ...f, fiscal_regime: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white" data-testid="invoice-fiscal-regime">
                  <option value="">Selecciona…</option>
                  {SAT_REGIMES.map(([c, l]) => <option key={c} value={c}>{c} — {l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Correo electrónico *</label>
                <input required type="email" value={invoiceForm.fiscal_email}
                  onChange={(e) => setInvoiceForm(f => ({ ...f, fiscal_email: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="invoice-fiscal-email" />
              </div>

              <p className="text-xs font-semibold uppercase tracking-wider text-[#1D3C6E] pt-2 border-t border-slate-100">Datos del CFDI</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Uso de CFDI *</label>
                  <select required value={invoiceForm.cfdi_use} onChange={(e) => setInvoiceForm(f => ({ ...f, cfdi_use: e.target.value }))}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white" data-testid="invoice-cfdi-use">
                    <option value="">Selecciona…</option>
                    {CFDI_USES.map(([c, l]) => <option key={c} value={c}>{c} — {l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Forma de Pago</label>
                  <select value={invoiceForm.payment_form} onChange={(e) => setInvoiceForm(f => ({ ...f, payment_form: e.target.value }))}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white" data-testid="invoice-payment-form">
                    {PAYMENT_FORMS.map(([c, l]) => <option key={c} value={c}>{c} — {l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Método de Pago</label>
                  <select value={invoiceForm.payment_method} onChange={(e) => setInvoiceForm(f => ({ ...f, payment_method: e.target.value }))}
                    className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white" data-testid="invoice-payment-method">
                    {PAYMENT_METHODS.map(([c, l]) => <option key={c} value={c}>{c} — {l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Notas (opcional)</label>
                <textarea value={invoiceForm.notes} onChange={(e) => setInvoiceForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm min-h-[60px]" data-testid="invoice-notes" />
              </div>

              <div className="flex items-start gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-[#1D3C6E]" />
                <span>La solicitud se registrará como <strong>pendiente</strong>. Cuando se conecte el proveedor CFDI (Facturapi), las solicitudes pendientes se enviarán automáticamente para emisión.</span>
              </div>

              {invoiceError && (
                <div className="flex items-start gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700" data-testid="invoice-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{invoiceError}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setInvoiceOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100" data-testid="invoice-cancel-btn">
                Cancelar
              </button>
              <button type="submit" disabled={invoiceSaving} className="px-4 py-2 rounded-lg text-sm font-medium bg-[#1D3C6E] text-white hover:bg-[#152B50] disabled:opacity-60" data-testid="invoice-submit-btn">
                {invoiceSaving ? 'Guardando…' : 'Solicitar Factura'}
              </button>
            </div>
          </form>
        </div>
      )}

      <ThermalTicket variant="sale" data={sale} branchName={branchName} />
    </div>
  );
}
